import { AlertServiceProvider } from '../../../core/services/alert-service/alert-service';
import { Component, ComponentFactoryResolver, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormField } from '../../../../submodules/sellions_forms/projects/forms/src/lib/interfaces/form-field.interface';
import { FormGroup } from '@angular/forms';
import { IonicPage, NavParams, PopoverController, ToastController, ViewController } from 'ionic-angular';
import { take } from 'rxjs/operators';
import { TextServiceProvider } from '../../../core/services/text-service/text-service';
import { UserTask } from '../../interfaces/user-task.interface';
import { UserTaskService } from '../../services/user-task.service/user-task.service';
import { ENVIRONMENT } from '../../../../../environments/environment';
import { UserTaskInfoPage } from './info/user-task-info';
import { FileDisplayerComponent } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/components/file-displayer/file-displayer.component';
import { FileVariable } from '../../interfaces/file-variable.interface';
import { FormDataService } from '../../../../submodules/sellions_forms/projects/forms/src/lib/services/form-data/form-data.service';
import { ErrorUtils } from '../../../../utils/error.utils';
import { SellionsFormsRendererComponent } from '../../../../submodules/sellions_forms/projects/forms/src/public-api';

@IonicPage()
@Component({
  selector: 'page-user-task-preview',
  templateUrl: 'user-task-preview.html',
})
export class UserTaskPreviewPage implements OnInit {
  public disabled: boolean;
  public userTask: UserTask;
  public dataViewBaseAPI = ENVIRONMENT.engineUrl;
  public form: FormField[];
  public json: any;
  public taskType;
  public assignButtonAvailable: any;
  public isSendButtonDisabled = false;

  @ViewChild('viewContainerRef', { read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  @ViewChild(SellionsFormsRendererComponent) sellionsFormsRendererComponent: SellionsFormsRendererComponent;
  fileDisplayerComponentInstances: Array<ComponentRef<FileDisplayerComponent>> = [];

  constructor(
    public textService: TextServiceProvider,
    private alertService: AlertServiceProvider,
    private navParams: NavParams,
    private userTaskService: UserTaskService,
    private viewCtrl: ViewController,
    private popoverCtrl: PopoverController,
    private toastController: ToastController,
    private componentFactoryResolver: ComponentFactoryResolver,
    private formDataService: FormDataService,
  ) {
    this.userTask = this.navParams.get('userTask');
    this.taskType = this.navParams.get('taskType');
    this.disabled = this.navParams.get('disabled');
    this.assignButtonAvailable = this.navParams.get('assignButtonAvailable');
  }

  public ngOnInit() {
    this.getUserTask(this.userTask.number);
  }

  public formChangeUpdate(form: FormGroup): void {
    this.json = form.getRawValue();
  }

  public getUserTask(id: string): void {
    this.userTaskService.get(id).subscribe(
      (userTask: UserTask) => this.handleUserTaskData(userTask),
      (error) => this.handleUserTaskFormError(error),
    );
  }

  private handleUserTaskData(userTask: UserTask): void {
    this.userTask = userTask;
    this.form = this.getForm(userTask);
    this.parseVariables(this.userTask.variables);
  }

  private getForm(task: UserTask): FormField[] {
    const form = JSON.parse(this.userTask.form) as FormField[];
    return form.map((field) => ({ ...field, predefinedValue: task.variables && task.variables[field.id] }));
  }

  private parseVariables(variables: { [key: string]: any }): void {
    for (const prop in variables) {
      const variable = variables[prop];

      if (variable === null) {
        continue;
      }

      if (Array.isArray(variable)) {
        this.parseArrayVariable(variable);
      } else if (this.isFileReference(variable)) {
        this.createFileDisplayerComponentInstance(variable);
      } else if (typeof variable === 'object') {
        this.parseVariables(variable);
      }
    }
  }

  private parseArrayVariable(arrayVariable: Array<any>): void {
    for (let element of arrayVariable) {
      if (this.isFileReference(element)) {
        this.createFileDisplayerComponentInstance(element);
      } else if (typeof element === 'object') {
        this.parseVariables(element);
      }
    }
  }

  private isFileReference(object: any | FileVariable): boolean {
    return (
      typeof object === 'object' &&
      'id' in object &&
      'contentType' in object &&
      'value' in object &&
      'hateoasUrl' in object.value &&
      'name' in object.value
    );
  }

  private createFileDisplayerComponentInstance(fileVariable: FileVariable): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(FileDisplayerComponent);
    const fileDisplayerComponent = this.viewContainerRef.createComponent(componentFactory);
    this.fileDisplayerComponentInstances.push(fileDisplayerComponent);

    fileDisplayerComponent.instance.baseUrl = this.dataViewBaseAPI;
    fileDisplayerComponent.instance.fileReference = { fileId: fileVariable.id, contentType: fileVariable.contentType };
    fileDisplayerComponent.instance.displayName = decodeURIComponent(fileVariable.value.name);
    const apiPath = this.getFileApiPath(fileVariable);
    fileDisplayerComponent.instance.fileDisplayerConfig = { apiPath, fieldName: undefined };
  }

  private getFileApiPath(fileVariable: FileVariable): string {
    const apiPath = fileVariable.value.hateoasUrl.replace(fileVariable.id, '');
    if (apiPath.startsWith('/')) {
      return apiPath.substring(1);
    }
    return apiPath;
  }

  private handleUserTaskFormError(error: any): void {
    const errorMsg = ErrorUtils.getErrorMessage(error);
    const errorAlert = this.alertService.getErrorAlert(`Niestety nie udało się pobrać formularza zadania zadania. ${errorMsg}`, 'Bląd');

    errorAlert.onDidDismiss(() => this.close());
    errorAlert.present();
  }

  public save() {
    if (!this.isFormValid() || this.isSendButtonDisabled) {
      this.alertService.showErrorAlert(`Formularz wypełniony niepoprawnie`, 'Bląd');
      return;
    }

    this.isSendButtonDisabled = true;
    const formVariables = this.formDataService.mapFormRawDataToFormVariables(this.json, this.form);
    this.userTaskService
      .complete(this.userTask.number, formVariables)
      .pipe(take(1))
      .subscribe(
        () => {
          this.showToastMessage('Pomyślnie zapisano zadanie');
          this.close();
        },
        (error) => this.handleError(error),
      );
  }

  private isFormValid(): boolean {
    return this.sellionsFormsRendererComponent.isFormValid();
  }

  private handleError(error: any): void {
    if (error.status === 0) {
      this.close();
    } else {
      const errorMsg = ErrorUtils.getErrorMessage(error);
      this.alertService.showErrorAlert(`Niestety nie udało się zrealizować zadania. ${errorMsg}`, 'Bląd');
      this.isSendButtonDisabled = false;
    }
  }

  public assign(): void {
    this.userTaskService.claim(this.userTask.number).subscribe(
      () => this.handleClaimSuccess(),
      (error) => this.handleClaimError(error),
    );
  }

  private handleClaimSuccess(): void {
    this.showToastMessage('Pomyślnie przypisano zadanie');
    this.close();
  }

  private handleClaimError(error: any): void {
    const errorMsg = ErrorUtils.getErrorMessage(error);
    this.alertService.showErrorAlert(`Niestety nie udało się przypisać zadania. ${errorMsg}`, 'Bląd');
  }

  private showToastMessage(message: string): void {
    this.toastController
      .create({
        message: message,
        position: 'top',
        duration: 5000,
      })
      .present();
  }

  async openUserTaskInfo(): Promise<void> {
    const modal = this.popoverCtrl.create(
      UserTaskInfoPage,
      {
        userTask: this.userTask,
        taskType: this.taskType,
      },
      { cssClass: 'user-task-preview' },
    );
    modal.present();
  }

  public close(): void {
    this.viewCtrl.dismiss();
  }
}



// WEBPACK FOOTER //
// ./src/modules/user-tasks/pages/user-task-preview/user-task-preview.ts